import { Input } from '@fountain/fountain-ui-components';
import { Condition } from 'api-clients/monolith';
import { produce } from 'immer';
import React, { FC, HTMLInputTypeAttribute, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Error } from 'components/Error';
import { getConditionErrorMessage } from 'containers/WorkflowEditor/components/Rules/Condition/constants';
import globalMessages from 'shared/global/messages';

import { DocumentSigningRuleProps } from '../../DocumentSigningRules/types';
import {
  PartnerRuleProps,
  SharedPartnerConditionProps,
} from '../../PartnerRules/types';
import { RulesProps, SharedConditionProps } from '../../types';

type FilteredKeys<T, U> = NonNullable<
  { [K in keyof T]: T[K] extends U | undefined ? K : never }[keyof T]
>;

export type ConditionInputKeys = FilteredKeys<
  Condition['extra'],
  string | number
>;

export type InputFieldProps = (
  | SharedConditionProps
  | SharedPartnerConditionProps
) & {
  valueKey?: ConditionInputKeys;
  inputType?: HTMLInputTypeAttribute;
  placeholder?: string;
  required?: boolean;
};

export const InputField: FC<InputFieldProps> = ({
  condition,
  ruleId,
  setRules,
  setPartnerRules,
  setDocumentSigningRules,
  valueKey = 'value',
  errors,
  inputType = 'text',
  placeholder,
  required = true,
}) => {
  const intl = useIntl();
  const { extra } = condition;
  const [inputValue, setInputValue] = useState(extra[valueKey] ?? '');

  useEffect(() => {
    setInputValue(extra[valueKey] ?? '');
  }, [extra, valueKey]);

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const onBlurInput = () => {
    const rulesSetter = setRules ?? setDocumentSigningRules;

    if (rulesSetter) {
      rulesSetter(
        produce((draftRules: RulesProps | DocumentSigningRuleProps) => {
          const draftConditions =
            draftRules[ruleId]?.condition_set_attributes
              ?.conditions_attributes ?? [];

          const draftCondition = draftConditions.find(
            cond => cond.id === condition.id,
          );
          if (draftCondition) {
            draftCondition.extra = {
              ...draftCondition.extra,
              [valueKey]: inputValue,
            };
          }
        }),
      );
    }

    if (setPartnerRules) {
      setPartnerRules(
        produce((draftRules: PartnerRuleProps) => {
          const draftCondition = draftRules[ruleId].condition_attributes;

          draftCondition.extra = {
            ...draftCondition.extra,
            [valueKey]: inputValue,
          };
        }),
      );
    }
  };

  const errorMessage =
    !extra[valueKey] && getConditionErrorMessage(valueKey, errors);

  return (
    <>
      <Input
        label={intl.formatMessage(globalMessages.value)}
        aria-label={intl.formatMessage(globalMessages.value)}
        fullWidth
        value={inputValue}
        onChange={onChangeInput}
        onBlur={onBlurInput}
        type={inputType}
        placeholder={placeholder}
        error={Boolean(errorMessage)}
        required={required}
      />
      <Error error={errorMessage} align="right" />
    </>
  );
};
